/**
 * Service-worker update lifecycle.
 *
 * Two audiences, two behaviours:
 *
 * 1. Public always-on surfaces (`/q/<slug>` TV displays, patient portal, token
 *    permalinks) live on a tablet for days and nobody hard-refreshes them — so
 *    when a new SW activates we reload them immediately.
 *
 * 2. Logged-in staff must NOT be reloaded from under their work (they may be
 *    mid-form). Instead we surface a dismissible "new version" banner they tap
 *    when ready. Separately, if a stale bundle hits an unknown route (usually a
 *    route added in a newer deploy that the old precached JS doesn't know),
 *    `refreshToLatest()` pulls the fresh bundle so the link "just works" instead
 *    of dead-ending on a 404.
 *
 * `virtual:pwa-register` is generated by vite-plugin-pwa at build time; during
 * dev it's a stub that no-ops.
 */
import { registerSW } from "virtual:pwa-register";

const AUTO_RELOAD_PREFIXES = [
	"/q/",
	"/c/",
	"/p/",
	"/portal",
	"/a/",
	"/rx/",
	"/lab/",
	"/receipt/",
];

function shouldAutoReload(): boolean {
	const path = window.location.pathname;
	return AUTO_RELOAD_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** Applies a waiting SW (skipWaiting) and reloads; set by registerSW below. */
let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;

/** A newer build is available (staff surfaces show a banner for this). */
let updateAvailable = false;
const listeners = new Set<(available: boolean) => void>();

/** Subscribe to "a new version is available" — returns an unsubscribe fn. */
export function onSwUpdateAvailable(
	fn: (available: boolean) => void,
): () => void {
	listeners.add(fn);
	fn(updateAvailable); // fire current state immediately
	return () => listeners.delete(fn);
}

/**
 * Pull and activate the latest build, then reload. Used by the update banner
 * and by the not-found guard (a stale bundle that doesn't know a new route).
 * Falls back to a plain reload when the SW isn't registered (e.g. dev).
 */
export function refreshToLatest(): void {
	if (updateSW) void updateSW(true);
	else window.location.reload();
}

export function bootstrapServiceWorker(): void {
	updateSW = registerSW({
		immediate: true,
		onNeedRefresh() {
			if (shouldAutoReload()) {
				window.location.reload();
				return;
			}
			// Staff: never yank the page — announce it and let them choose.
			updateAvailable = true;
			for (const fn of listeners) fn(true);
		},
		onOfflineReady() {
			// Intentionally silent — the shell becomes offline-capable transparently.
		},
	});
}
